/**
 * The tables of ship classes, one for movement and one for combat: every class with its figures,
 * read from the game's ruleset (R-FLEET-01), so a game with changed classes reads its own table.
 */
import type { Ruleset, ShipClass, ShipClassId } from '@brig/rules-engine';
import { visuallyHidden } from '@brig/theme/styles';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ShipToken } from './ShipToken.tsx';
import * as css from './ShipsTable.css.ts';

export type ShipsTableKind = 'movement' | 'combat';

/** Needs <ShipSprite> on the page for the fallback art. */
export function ShipsTable({ ruleset, kind }: { ruleset: Ruleset; kind: ShipsTableKind }) {
  const { t } = useTranslation();
  const classes = Object.keys(ruleset.shipClasses) as ShipClassId[];
  const caption = t(`shipsPage.${kind}.title`);
  const columns: readonly { head: string; cell: (id: ShipClassId, cls: ShipClass) => ReactNode }[] =
    kind === 'movement'
      ? [
          {
            head: t('shipsPage.table.propulsion'),
            cell: (_id, cls) => (
              <span className={css.text}>{t(`shipsPage.table.kinds.${cls.propulsion}`)}</span>
            ),
          },
          { head: t('ships.card.fresh'), cell: (_id, cls) => cls.freshSpeed },
          {
            head: t('ships.card.calm'),
            cell: (_id, cls) => cls.calmSpeed || <span title={t('ships.card.cannotMove')}>—</span>,
          },
          { head: t('ships.card.storm'), cell: (_id, cls) => cls.stormDrift },
        ]
      : [
          {
            head: t('shipsPage.table.guns'),
            cell: (id) => <span className={css.text}>{t(`ships.guns.${id}`)}</span>,
          },
          { head: t('ships.card.shots'), cell: (_id, cls) => cls.shotsPerTurn },
          { head: t('ships.card.range'), cell: (_id, cls) => cls.range },
          { head: t('ships.card.sinksAt'), cell: (_id, cls) => cls.sinksAt },
          { head: t('shipsPage.table.inFleet'), cell: (id) => ruleset.fleet[id] ?? 0 },
        ];
  return (
    <div className={css.scroller} role="region" aria-label={caption} tabIndex={0}>
      <table className={css.table} data-kind={kind}>
        {/* The page heads each table itself. */}
        <caption className={visuallyHidden}>{caption}</caption>
        <thead>
          <tr>
            <th scope="col" className={css.nameHead}>
              {t('shipsPage.table.ship')}
            </th>
            {columns.map((c) => (
              <th key={c.head} scope="col">
                {c.head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {classes.map((id) => {
            const cls = ruleset.shipClasses[id];
            return (
              <tr key={id} data-ship={id}>
                <th scope="row" className={css.name}>
                  <span className={css.ship}>
                    {/* No flag: the table speaks of classes, not of any nation's ships. */}
                    <ShipToken type={id} nation="spain" size={56} flag={false} shadow={false} />
                    {t(`ships.names.${id}`)}
                  </span>
                </th>
                {columns.map((c) => (
                  <td key={c.head}>{c.cell(id, cls)}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
